/**
 * Tree layout.
 *
 * Turns the graph the server stores into positions on a page. The graph is the
 * truth; this is only a rendering of it.
 *
 * **The rule that shapes everything here.** A person may occupy more than one
 * position — marry your cousin and you belong both beside your spouse and below
 * your own parents. Exactly one parent edge is marked `is_anchor` by the server,
 * and that is where the person is *drawn*. Every other parent edge becomes a
 * dashed cross-link. So a person is laid out once, never twice: one node, one
 * profile, counted once.
 *
 * The pass is O(n): place each household's children first, then centre the adults
 * over them, shifting subtrees right on collision. Same approach as the prototype,
 * which is what keeps it usable on a phone.
 */

/**
 * A node is a circle with its name written underneath, as the prototype draws it.
 *
 * `NODE_W` and `NODE_H` describe the *slot* a person occupies, not the circle: the slot has
 * to be wide enough for the name under the circle, or four generations of labels overlap
 * into a smear. The circle itself sits at the top of the slot, centred horizontally, which
 * is why every connector anchors on `CIRCLE_R` rather than on half the slot height.
 */
export const CIRCLE_R = 27;
export const LABEL_H = 34; // name and dates, under the circle
export const NODE_W = 96;
export const NODE_H = CIRCLE_R * 2 + LABEL_H;
const COUPLE_GAP = 26; // between two spouses in one household
const SIBLING_GAP = 34; // between sibling subtrees
const GENERATION_H = 148; // vertical distance between generations

/**
 * Build households from the edge list.
 *
 * A "household" is one or two adults plus the children anchored to them. It is
 * the unit the eye reads on a family tree — not the individual, and not the
 * marriage, because a single parent is still a household.
 */
function buildHouseholds(people, edges) {
  const byId = new Map(people.map((p) => [p.id, p]));

  const spouseEdges = edges.filter((e) => e.type === 'spouse');
  const parentEdges = edges.filter((e) => e.type === 'parent');

  // --- Group spouses into households ------------------------------------- //
  const householdOf = new Map(); // personId -> householdId
  const households = new Map(); // householdId -> household

  const makeHousehold = (id) => {
    const household = {
      id,
      adults: [],
      leafChildren: [], // unmarried children, drawn directly under
      childHouseholds: [], // married children, each heading their own household
      x: 0,
      y: 0,
      width: 0,
      depth: 0,
      parentHouseholdId: null,
    };
    households.set(id, household);
    return household;
  };

  for (const edge of spouseEdges) {
    const a = edge.from_person;
    const b = edge.to_person;
    if (!byId.has(a) || !byId.has(b)) continue;

    // Remarriage: a person can appear in more than one spouse edge. The first
    // household they land in is where they are drawn; later marriages become
    // their own households whose adults list includes the other spouse only.
    const existing = householdOf.get(a) ?? householdOf.get(b);
    const household = existing ? households.get(existing) : makeHousehold(`hh:${a}:${b}`);

    for (const personId of [a, b]) {
      if (!householdOf.has(personId)) {
        householdOf.set(personId, household.id);
        household.adults.push(personId);
      }
    }
  }

  // Anyone not in a spouse edge gets a household of one, so a single parent and
  // an unmarried adult both still have somewhere to stand.
  for (const person of people) {
    if (householdOf.has(person.id)) continue;
    const household = makeHousehold(`hh:${person.id}`);
    household.adults.push(person.id);
    householdOf.set(person.id, household.id);
  }

  // --- Attach children to the household of their anchor parent ------------ //
  const anchorParentOf = new Map(); // childId -> parentId (the anchor edge)
  const crossLinks = []; // every non-anchor parent edge

  for (const edge of parentEdges) {
    if (edge.status === 'disputed') continue;
    if (!byId.has(edge.from_person) || !byId.has(edge.to_person)) continue;

    if (edge.is_anchor) anchorParentOf.set(edge.to_person, edge.from_person);
    else crossLinks.push({ childId: edge.to_person, parentId: edge.from_person, edge });
  }

  // Who heads a household of their own, rather than being a leaf under their
  // parents? Anyone with a spouse, or with children of their own.
  //
  // Computed as its own pass on purpose. Deciding this inline while attaching
  // children makes the answer depend on iteration order: a single parent processed
  // before their child looks childless, so their household never gets hung under
  // their own parents — and the child lands in the oldest generation. That put
  // Musa, a great-grandson, in the same row as his great-grandfather.
  const hasChildren = new Set(parentEdges.map((edge) => edge.from_person));

  const headsOwnHousehold = (personId) => {
    const household = households.get(householdOf.get(personId));
    return Boolean(household && (household.adults.length > 1 || hasChildren.has(personId)));
  };

  for (const person of people) {
    const anchorParent = anchorParentOf.get(person.id);
    if (!anchorParent) continue;

    const parentHouseholdId = householdOf.get(anchorParent);
    const ownHouseholdId = householdOf.get(person.id);
    const parentHousehold = households.get(parentHouseholdId);
    if (!parentHousehold) continue;

    const ownHousehold = households.get(ownHouseholdId);

    if (ownHousehold && headsOwnHousehold(person.id)) {
      // A household with two adults who both have parents on this tree can only
      // hang under one of them. It hangs under the FIRST adult's parents — first
      // by the household's own adult order, so the choice is stable across
      // renders rather than depending on iteration order. The other adult's line
      // to their parents becomes the dashed cross-link, and that is precisely the
      // cousin-marriage case made visible.
      const isPrimaryAdult = ownHousehold.adults[0] === person.id;

      if (isPrimaryAdult || ownHousehold.parentHouseholdId === null) {
        ownHousehold.parentHouseholdId = parentHouseholdId;
        ownHousehold.hangsFromPersonId = person.id;
        if (!parentHousehold.childHouseholds.includes(ownHouseholdId)) {
          parentHousehold.childHouseholds.push(ownHouseholdId);
        }
      }
    } else {
      // No spouse and no children: drawn as a leaf directly under their parents.
      parentHousehold.leafChildren.push(person.id);
    }
  }

  // --- Which parent edges are genuinely a second position? ---------------- //
  //
  // Not every non-anchor parent edge is one. A child with two parents has two
  // parent edges and only one anchor, but both parents live in the SAME household
  // — the child is in one position with two parents above them, which is the
  // ordinary case and needs no dashed line. Drawing one there would put an arc on
  // every person on the tree.
  //
  // A real second position is a parent edge reaching into a DIFFERENT household
  // from the one the person is drawn under.
  const meaningfulCrossLinks = [];

  for (const link of crossLinks) {
    const anchorParent = anchorParentOf.get(link.childId);
    const anchorHouseholdId = anchorParent ? householdOf.get(anchorParent) : null;
    const thisParentHouseholdId = householdOf.get(link.parentId);

    if (anchorHouseholdId && anchorHouseholdId === thisParentHouseholdId) continue;

    meaningfulCrossLinks.push(link);
  }

  // The other half of the same idea, from the household's side: a married person
  // whose household hangs under their spouse's parents still has their own parents
  // on the tree. That line is a second position and must be drawn.
  for (const household of households.values()) {
    if (household.adults.length < 2 || !household.hangsFromPersonId) continue;

    for (const adultId of household.adults) {
      if (adultId === household.hangsFromPersonId) continue;

      const ownParent = anchorParentOf.get(adultId);
      if (!ownParent) continue;

      const ownParentHouseholdId = householdOf.get(ownParent);
      if (ownParentHouseholdId === household.parentHouseholdId) continue;

      const already = meaningfulCrossLinks.some(
        (link) => link.childId === adultId && link.parentId === ownParent,
      );
      if (!already) {
        meaningfulCrossLinks.push({ childId: adultId, parentId: ownParent, edge: null });
      }
    }
  }

  return {
    households,
    householdOf,
    byId,
    crossLinks: meaningfulCrossLinks,
    anchorParentOf,
  };
}

/** Width a household needs for its adults alone. */
function adultsWidth(household) {
  const n = Math.max(1, household.adults.length);
  return n * NODE_W + (n - 1) * COUPLE_GAP;
}

/**
 * Lay out one household and everything beneath it.
 *
 * Children are measured first, then the adults are centred over them — so a
 * couple with five children sits above the middle of the five, not to one side.
 */
function layoutHousehold(household, households, depth, cursorX) {
  household.depth = depth;
  household.y = depth * GENERATION_H;

  const childUnits = [
    ...household.leafChildren.map((personId) => ({ type: 'person', personId })),
    ...household.childHouseholds.map((id) => ({ type: 'household', id })),
  ];

  if (childUnits.length === 0) {
    household.width = adultsWidth(household);
    household.x = cursorX;
    household.childrenSpan = null;
    return cursorX + household.width;
  }

  // Place the children first, left to right.
  let childCursor = cursorX;
  const childPositions = [];

  for (const unit of childUnits) {
    if (unit.type === 'person') {
      childPositions.push({ ...unit, x: childCursor, width: NODE_W });
      childCursor += NODE_W + SIBLING_GAP;
    } else {
      const child = households.get(unit.id);
      const end = layoutHousehold(child, households, depth + 1, childCursor);
      childPositions.push({ ...unit, x: child.x, width: child.width });
      childCursor = end + SIBLING_GAP;
    }
  }

  const childrenStart = childPositions[0].x;
  const last = childPositions[childPositions.length - 1];
  const childrenEnd = last.x + last.width;
  const childrenWidth = childrenEnd - childrenStart;

  household.childPositions = childPositions;
  household.childrenSpan = [childrenStart, childrenEnd];

  // Centre the adults over the children, then take whichever is wider as the
  // household's footprint so the next sibling subtree does not overlap.
  const ownWidth = adultsWidth(household);
  household.x = childrenStart + (childrenWidth - ownWidth) / 2;

  if (household.x < cursorX) {
    // The adults are wider than their children: shift the whole subtree right
    // rather than let the couple hang off the left edge.
    const shift = cursorX - household.x;
    shiftSubtree(household, households, shift);
    household.x = cursorX;
  }

  household.width = Math.max(ownWidth, childrenEnd - household.x);
  return household.x + household.width;
}

/** Move a household and every descendant right by `dx`. */
function shiftSubtree(household, households, dx) {
  household.x += dx;
  if (household.childrenSpan) {
    household.childrenSpan = [household.childrenSpan[0] + dx, household.childrenSpan[1] + dx];
  }
  for (const position of household.childPositions ?? []) {
    position.x += dx;
    if (position.type === 'household') {
      shiftSubtree(households.get(position.id), households, dx);
    }
  }
}

/**
 * Compute node positions for the whole tree.
 *
 * Returns nodes, connector lines, dashed cross-links, and the overall bounds.
 */
export function layoutTree(people, edges) {
  const { households, householdOf, byId, crossLinks } = buildHouseholds(people, edges);

  // Every person without a spouse also has a household of one, so that a single
  // parent has somewhere to stand. But a person drawn as a *leaf* under their
  // parents is already placed, and their spare household must not be treated as a
  // root — otherwise it lands in the oldest generation and the person appears to
  // float above their own family. That is what put Musa, a great-grandson, in the
  // same row as his great-grandfather.
  const leafPersons = new Set();
  for (const household of households.values()) {
    household.leafChildren.forEach((personId) => leafPersons.add(personId));
  }

  const isSpareHousehold = (household) =>
    household.adults.length === 1 &&
    leafPersons.has(household.adults[0]) &&
    household.leafChildren.length === 0 &&
    household.childHouseholds.length === 0;

  // Roots are households with no parent household — the oldest generation the
  // family has recorded. There can be several: a tree that has grown by marriage
  // often has two or three unconnected top couples.
  const roots = [...households.values()].filter(
    (h) => h.parentHouseholdId === null && !isSpareHousehold(h),
  );

  let cursor = 0;
  for (const root of roots) {
    cursor = layoutHousehold(root, households, 0, cursor) + SIBLING_GAP * 2;
  }

  // --- Flatten to nodes --------------------------------------------------- //
  const nodes = [];
  const nodeOf = new Map(); // personId -> node

  const addNode = (personId, x, y) => {
    const person = byId.get(personId);
    if (!person || nodeOf.has(personId)) return;
    const node = { person, x, y, w: NODE_W, h: NODE_H };
    nodes.push(node);
    nodeOf.set(personId, node);
  };

  for (const household of households.values()) {
    // A spare single-person household was never laid out, so its coordinates are
    // still (0, 0). Adding its adult here would place the person in the top-left
    // corner instead of the leaf position their parents' household gave them.
    if (isSpareHousehold(household)) continue;

    household.adults.forEach((personId, index) => {
      addNode(personId, household.x + index * (NODE_W + COUPLE_GAP), household.y);
    });

    for (const position of household.childPositions ?? []) {
      if (position.type === 'person') {
        addNode(position.personId, position.x, household.y + GENERATION_H);
      }
    }
  }

  // Anyone the layout never reached — an isolated record with no edges yet. Show
  // them rather than drop them: a relative someone added and has not linked is
  // exactly the gap the product wants a human to notice.
  let orphanX = cursor;
  for (const person of people) {
    if (nodeOf.has(person.id)) continue;
    addNode(person.id, orphanX, 0);
    orphanX += NODE_W + SIBLING_GAP;
  }
  cursor = Math.max(cursor, orphanX);

  // --- Connectors --------------------------------------------------------- //
  const links = [];

  for (const household of households.values()) {
    // Marriage bar between two adults.
    if (household.adults.length > 1) {
      const a = nodeOf.get(household.adults[0]);
      const b = nodeOf.get(household.adults[1]);
      if (a && b) {
        // Edge of one circle to edge of the other, on the circles' centre line — not on
        // the slot's, which sits halfway down the name underneath.
        links.push({
          kind: 'marriage',
          x1: a.x + a.w / 2 + CIRCLE_R,
          y1: a.y + CIRCLE_R,
          x2: b.x + b.w / 2 - CIRCLE_R,
          y2: b.y + CIRCLE_R,
        });
      }
    }

    if (!household.childPositions?.length) continue;

    const parentCentre = household.x + adultsWidth(household) / 2;

    // Starts at the circles' lower edge rather than the bottom of the slot: it runs down
    // *between* the spouses, so it never crosses a name.
    const dropY = household.y + CIRCLE_R * 2;
    const busY = household.y + NODE_H + (GENERATION_H - NODE_H) / 2;

    links.push({ kind: 'drop', x1: parentCentre, y1: dropY, x2: parentCentre, y2: busY });

    const childCentres = household.childPositions.map((position) =>
      position.type === 'person'
        ? position.x + NODE_W / 2
        : households.get(position.id).x + adultsWidth(households.get(position.id)) / 2,
    );

    links.push({
      kind: 'bus',
      x1: Math.min(parentCentre, ...childCentres),
      y1: busY,
      x2: Math.max(parentCentre, ...childCentres),
      y2: busY,
    });

    household.childPositions.forEach((position, index) => {
      const centre = childCentres[index];
      const childY =
        position.type === 'person'
          ? household.y + GENERATION_H
          : households.get(position.id).y;
      links.push({ kind: 'riser', x1: centre, y1: busY, x2: centre, y2: childY });
    });
  }

  // --- Cross-links: the second position a person occupies ----------------- //
  //
  // This is the cousin-marriage case made visible. Haris is drawn once, beside
  // Asifa, and a dashed line runs back up to Fauzia and Tariq because he is still
  // their son. One node, one profile, one birthday.
  const crossLinkLines = [];

  for (const link of crossLinks) {
    const childNode = nodeOf.get(link.childId);
    const parentNode = nodeOf.get(link.parentId);
    if (!childNode || !parentNode) continue;

    crossLinkLines.push({
      childId: link.childId,
      parentId: link.parentId,
      x1: childNode.x + childNode.w / 2,
      y1: childNode.y,
      x2: parentNode.x + parentNode.w / 2,
      y2: parentNode.y + CIRCLE_R * 2,
      label: `also child of ${parentNode.person.display_name}`,
    });
  }

  // Nodes that sit in more than one position get a second dashed ring.
  const multiPosition = new Set(crossLinkLines.map((line) => line.childId));

  const xs = nodes.map((n) => n.x);
  const ys = nodes.map((n) => n.y);

  return {
    nodes,
    links,
    crossLinks: crossLinkLines,
    multiPosition,
    bounds: {
      minX: Math.min(0, ...xs) - 40,
      minY: Math.min(0, ...ys) - 40,
      width: Math.max(cursor, ...xs.map((x) => x + NODE_W)) + 80,
      height: Math.max(...ys.map((y) => y + NODE_H)) + 80,
    },
  };
}
